import { AxiosError, AxiosHeaders, AxiosInstance } from 'axios'

import {
  extractCodeFromErrorResponse,
  extractMessageFromErrorResponse,
  getResponseStatus
} from 'shared/utils/errors'

export const SUPPORT_SESSION_CONFIRMED_HEADER = 'X-Support-Session-Confirmed'
export const SUPPORT_SESSION_CONFIRMATION_STATUS = 428

const CONFIRMATION_REQUIRED_CODE = 'support_session_confirmation_required'
const CONFIRMATION_FALLBACK_MESSAGE =
  'Ta operacja jest wiążąca finansowo dla klienta i w sesji wsparcia wymaga potwierdzenia.'
const CONFIRMATION_QUESTION = 'Kontynuować?'

interface SupportSessionRetryConfig {
  supportSessionConfirmed?: boolean
}

export const isSupportSessionConfirmationRequired = (error: unknown): boolean =>
  getResponseStatus(error) === SUPPORT_SESSION_CONFIRMATION_STATUS &&
  extractCodeFromErrorResponse(error) === CONFIRMATION_REQUIRED_CODE

/**
 * Turns the API's 428 response for request guarded by support session confirmation
 * into a confirmation prompt and, once the employee accepts, repeats the request 
 * once with the confirmation header. Declining rethrows the original error,
 * so each caller's own error handling still runs.
 */
export const applySupportSessionConfirmation = (instance: AxiosInstance) => {
  instance.interceptors.response.use(undefined, (error: AxiosError) => {
    const config = error.config as
      | (NonNullable<AxiosError['config']> & SupportSessionRetryConfig)
      | undefined

    if (
      typeof window === 'undefined' ||
      !config ||
      config.supportSessionConfirmed ||
      !isSupportSessionConfirmationRequired(error)
    ) {
      throw error
    }

    const message = extractMessageFromErrorResponse(
      error,
      CONFIRMATION_FALLBACK_MESSAGE
    )

    if (!window.confirm(`${message}\n\n${CONFIRMATION_QUESTION}`)) {
      throw error
    }

    // Guards against a second round trip if the API answers 428 again.
    config.supportSessionConfirmed = true

    const headers = AxiosHeaders.from(config.headers)

    headers.set(SUPPORT_SESSION_CONFIRMED_HEADER, '1')
    config.headers = headers

    return instance.request(config)
  })
}
